import { PenTool, Store, Truck, DollarSign } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      icon: PenTool,
      title: 'Create unique products',
      description:
        'Customize premium blank products with your designs using our easy-to-use design tools or upload your own artwork.',
    },
    {
      number: '02',
      icon: Store,
      title: 'Fill your store',
      description:
        'Connect your online store to Coastal Route and add your custom designs in just a few clicks, with no inventory or upfront costs.',
    },
    {
      number: '03',
      icon: Truck,
      title: 'We handle fulfillment',
      description:
        'When customers order, we handle production, quality control, and delivery—shipping your creations directly to them.',
    },
    {
      number: '04',
      icon: DollarSign,
      title: 'Earn profit',
      description:
        'You keep the difference between production costs and your selling price as profit. Scale without limits.',
    },
  ];

  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="py-24 md:py-32 bg-coastal-offwhite"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2
            data-testid="how-it-works-heading"
            className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold text-coastal-navy mb-6"
          >
            You dream it.{' '}
            <span className="text-coastal-orange">We deliver it.</span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            From design to doorstep in four simple steps. Focus on creating
            amazing products while we handle the rest.
          </p>
        </div>

        {/* Steps Grid */}
        <div
          data-testid="steps-grid"
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
        >
          {steps.map((step, index) => {
            const Icon = step.icon;

            return (
              <div
                key={step.number}
                data-testid={`step-${step.number}`}
                className="relative group"
              >
                {/* Connector Line (desktop) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-coastal-accent to-transparent" />
                )}

                {/* Card */}
                <div className="relative bg-white rounded-3xl p-8 border border-gray-200 hover:border-coastal-orange/30 hover:shadow-xl transition-all duration-300 h-full">
                  {/* Large Background Number */}
                  <span className="absolute top-4 right-4 text-7xl font-heading font-bold text-gray-100 select-none">
                    {step.number}
                  </span>

                  {/* Icon */}
                  <div className="relative z-10 w-16 h-16 bg-coastal-navy rounded-2xl flex items-center justify-center mb-6 group-hover:bg-coastal-orange transition-colors duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="relative z-10 font-heading font-semibold text-xl text-coastal-navy mb-3">
                    {step.title}
                  </h3>
                  <p className="relative z-10 text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="#contact"
            data-testid="how-it-works-cta"
            className="inline-flex items-center gap-2 bg-coastal-orange hover:bg-coastal-orange/90 text-white rounded-full px-8 py-4 font-semibold transition-all hover:-translate-y-1 shadow-lg hover:shadow-xl"
          >
            Start Earning Today
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
